import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, ImagePlus, Pencil } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p?.startsWith("http") ? p : `${BACKEND}/api/files/${p}`);

// Reusable hero image: shows the photo, or a tap-to-add placeholder.
// Uploads to object storage and calls onUploaded(path) so the parent can persist it.
export const HeroImageUpload = ({ imagePath, onUploaded, label = "Add photo", className = "", rounded = "rounded-xl", testid = "hero-image" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => { if (!busy) inputRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onUploaded(res.data.path);
      toast.success("Photo updated");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`relative group ${className}`} data-testid={testid}>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
      {imagePath ? (
        <>
          <img src={imgUrl(imagePath)} alt="" className={`h-full w-full object-cover ${rounded}`} />
          <button onClick={pick} disabled={busy} data-testid={`${testid}-change`}
            className={`absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-black/60 text-white text-xs font-medium px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity ${busy ? "opacity-100" : ""}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />} Change
          </button>
        </>
      ) : (
        <button onClick={pick} disabled={busy} data-testid={`${testid}-add`}
          className={`h-full w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 text-slate-400 hover:border-lime-400 hover:text-lime-600 hover:bg-lime-50 transition-colors ${rounded}`}>
          {busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <><ImagePlus className="h-7 w-7" /><span className="text-sm font-medium flex items-center gap-1"><Camera className="h-4 w-4" /> {label}</span><span className="text-[11px]">Tap to upload or take a photo</span></>}
        </button>
      )}
    </div>
  );
};

export default HeroImageUpload;
